import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type DeliveryNotesAdminQueryParameters,
  type DeliveryNotesAdminResponse,
} from './types';
import { zodDeliveryNotesAdminQueryParametersSchema } from './validations';

/**
 * Fetches all Delivery Notes from the server that match the given filter configuration.
 *
 * @param queryParameters - Query parameters including filters, pagination, and sorting.
 * @returns A paginated response of Delivery Notes.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/search_delivery_note_vadmin_asset_delivery_note_search_post
 */
export const fetchDeliveryNotesAdmin = async (
  queryParameters: DeliveryNotesAdminQueryParameters,
  options?: KyRequestOptions,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.DELIVERY.GET_ALL_ADMIN(), {
        json: queryParameters,
        validate: { body: zodDeliveryNotesAdminQueryParametersSchema },
        ...options,
      })
      .json<DeliveryNotesAdminResponse>();

    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Notes Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.deliveryNote.getAllAdmin.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getAllAdmin.error.generic,
            {
              ns: 'data',
            },
          );
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
